import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {CustomStore} from 'devextreme-angular/common/data';
import DataSource from 'devextreme/data/data_source';
import {lastValueFrom, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import * as AspNetData from "devextreme-aspnet-data-nojquery";
import {
    AgencyTimeOffGridViewDto,
    AssetGridViewDto,
    AssetType,
    BinGridViewDto,
    ChangeRequestGridViewDto,
    ChecklistGridViewDto,
    CountsPerStoreTypeGridViewDto,
    CountsPerPalletTypeGridViewDto,
    CreateDurationSettingCommand,
    EmailLogGridViewDto,
    EmailTemplateGridViewDto,
    FacilityUtilizationGridViewDto,
    FloaterDayGridViewDto,
    HalbGridViewDto,
    IncidentReportGridViewDto,
    InventoryClient,
    LeaveOfAbsenceGridViewDto,
    LieuDayGridViewDto,
    MaterialGridViewDto,
    PersonalTimesGridViewDto,
    RecipientOptionDto,
    StorageUnitGridViewDto,
    StorageUnitByBinGridViewDto,
    UpdateDurationSettingCommand,
    UserGridViewDto,
    VacationGridViewDto,
    CountType,
    PalletCountType,
    AppointmentGridViewDto,
    AppointmentHistoryGridViewDto,
    ShuntLocationType,
    AppointmentComplianceGridViewDto,
    PurchaseOrderGridViewDto,
    OrderGridViewDto,
    OrderHistoryGridViewDto,
    OrderLineDto,
    LoadGridViewDto,
    EmptyCheckInGridViewDto,
    EmptyCheckInHistoryGridViewDto,
    EmptyCheckOutGridViewDto,
    EmptyCheckOutHistoryGridViewDto,
    PurchaseOrderItemDto,
    BlockedStockGridViewDto,
    BlockedStockScoreGridViewDto,
    NextViewGridViewDto,
    YearToDateGridViewDto,
    PerformanceGridViewDto,
    MB51GridViewDto,
    MB51QuantityGridViewDto,
    MB51Reason,
    ProductivityClient,
    CreateProductivitySettingCommand,
    UpdateProductivitySettingCommand,
    CreateGlCodeCommand,
    UpdateGlCodeCommand,
    InvoicingClient,
    InvoicePurchaseOrderGridViewDto,
    InvoicePurchaseOrderHistoryGridViewDto,
    CreateServiceTypeCommand,
    UpdateServiceTypeCommand,
    CreateGlCodeClientCommand,
    UpdateGlCodeClientCommand,
    UpdateConversionRateCommand,
    CreateConversionRateCommand,
    MustDepartTimesClient,
    CreateMustDepartTimeCommand,
    UpdateMustDepartTimeCommand,
    CustomersClient,
    CreateCustomerCommand,
    UpdateCustomerCommand,
    InvoiceHistoryGridViewDto,
    InvoiceGridViewDto,
    CreditRequestGridViewDto,
    CreditRequestHistoryGridViewDto,
    VendorStatus,
    NestleDeactivationGridViewDto,
    UserHistoryGridViewDto,
} from "@services/web-api-client";
import {LoadOptions} from "devextreme/common/data";
import {UserRole} from "../shared/constants";

const createDate = (val?: string) => {
    if (!val) return;

    const [y, m, d] = val.split("T")[0].split("-").map(Number);
    return new Date(y, m - 1, d);
};

@Injectable({
    providedIn: 'root'
})
export class DataSources {
    private inventoryClient = inject(InventoryClient);
    private productivityClient = inject(ProductivityClient);
    private invoicingClient = inject(InvoicingClient);
    private mustDepartTimesClient = inject(MustDepartTimesClient);
    private customersClient = inject(CustomersClient);
    private http = inject(HttpClient);

    private handleError(errors: any[]) {
        let errorMessage = errors.map(err => err.message).join('\n');
        return throwError(() => errorMessage);
    }

    private buildParams(loadOptions: LoadOptions<any>) {
        let params = new HttpParams();

        if (loadOptions.skip !== undefined) params = params.set('skip', loadOptions.skip);
        if (loadOptions.take !== undefined) params = params.set('take', loadOptions.take);
        if (loadOptions.requireTotalCount !== undefined) params = params.set('requireTotalCount', loadOptions.requireTotalCount);
        if (loadOptions.requireGroupCount !== undefined) params = params.set('requireGroupCount', loadOptions.requireGroupCount);

        if (loadOptions.sort) params = params.set('sort', JSON.stringify(loadOptions.sort));
        if (loadOptions.filter) params = params.set('filter', JSON.stringify(loadOptions.filter));
        if (loadOptions.totalSummary) params = params.set('totalSummary', JSON.stringify(loadOptions.totalSummary));
        if (loadOptions.group) params = params.set('group', JSON.stringify(loadOptions.group));
        if (loadOptions.groupSummary) params = params.set('groupSummary', JSON.stringify(loadOptions.groupSummary));

        return params;
    }

    public durationSettings = (storageTypeCode: string | null = null) => new CustomStore({
        key: 'id',
        load: (loadOptions) => {
            let params = this.buildParams(loadOptions);
            let headers = {
                'StorageTypeCode': storageTypeCode
            };
            return lastValueFrom(this.http.get<any>('/api/Inventory/GetDurationSettings', {params, headers, withCredentials: true}));
        },
        insert: (values) => {
            const command = new CreateDurationSettingCommand();
            command.init(values);
            return lastValueFrom(this.inventoryClient.createDurationSetting(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        update: (key, values) => {
            const command = new UpdateDurationSettingCommand();
            command.id = key;
            command.init(values);
            return lastValueFrom(this.inventoryClient.updateDurationSetting(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        remove: (key) => lastValueFrom(this.inventoryClient.deleteDurationSetting(key))
    });

    public roleOptions = (key: string = 'name') => AspNetData.createStore({
        key: key,
        loadUrl: `/api/Roles/getRoleOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public employeeRoleOptions = () => new DataSource({
        store: this.roleOptions(),
        filter: [
            ['name', '=', UserRole.AgencyEmployee], 'or',
            ['name', '=', UserRole.WarehouseEmployee], 'or',
            ['name', '=', UserRole.OfficeEmployee],
        ],
        paginate: false,
    });

    public roles = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Roles/getRoleList`,
        deleteUrl: `/api/Roles/deleteRole`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public shiftOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Shifts/GetShiftOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public shifts = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Shifts/getShiftList`,
        deleteUrl: `/api/Shifts/deleteShift`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public supervisorOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Employees/getSupervisorOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public employeeOptions = (module: string | null = null) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Employees/getEmployeeOptions${module ? `?id=${module}` : ''}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public shuntProductivityUserOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/YardManagement/GetShuntProductivityUserOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public unloadEmployeeOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/EmptyCheckIns/GetUnloadEmployeeOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public employeeList = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Employees/getEmployeeList`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
    });

    public fileImports = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/FileImports/GetFileImports`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
    });

    public timeLogs = (employeeId: string) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Employees/GetTimeLogs`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
            ajaxOptions.headers = {
                'EmployeeId': employeeId
            };
        }
    });

    public huddles = () => AspNetData.createStore({
        key: 'employeeId',
        loadUrl: `/api/Employees/GetHuddleList`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public vacations(employeeId: string) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/TimeOff/GetVacations`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'EmployeeId': employeeId
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new VacationGridViewDto();
                    data.init(item);
                    data.requestedStartDate = createDate(item.requestedStartDate);
                    data.requestedEndDate = createDate(item.requestedEndDate);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public floaterDays(employeeId: string) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/TimeOff/GetFloaterDays`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'EmployeeId': employeeId
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new FloaterDayGridViewDto();
                    data.init(item);
                    data.requestedStartDate = createDate(item.requestedStartDate);
                    data.requestedEndDate = createDate(item.requestedEndDate);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public leaveOfAbsences(employeeId: string) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/TimeOff/GetLeaveOfAbsences`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'EmployeeId': employeeId
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new LeaveOfAbsenceGridViewDto();
                    data.init(item);
                    data.requestedStartDate = createDate(item.requestedStartDate);
                    data.requestedEndDate = createDate(item.requestedEndDate);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public agencyTimeOffs(employeeId: string) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/TimeOff/GetAgencyTimeOffs`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'EmployeeId': employeeId
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new AgencyTimeOffGridViewDto();
                    data.init(item);
                    data.requestedStartDate = createDate(item.requestedStartDate);
                    data.requestedEndDate = createDate(item.requestedEndDate);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public lieuDays(employeeId: string) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/TimeOff/GetLieuDays`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'EmployeeId': employeeId
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new LieuDayGridViewDto();
                    data.init(item);
                    data.requestedDate = createDate(item.requestedDate);
                    data.expireDate = createDate(item.expireDate);
                    data.takenBankSlotDate = createDate(item.takenBankSlotDate);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public personalTimes(employeeId: string) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/TimeOff/GetPersonalTimes`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'EmployeeId': employeeId
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new PersonalTimesGridViewDto();
                    data.init(item);
                    data.requestedDate = createDate(item.requestedDate);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public userList = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Users/getUserList`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new UserGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public nestleDeactivationHistory = (userId: string) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Employees/GetNestleDeactivationHistory?id=${userId}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new NestleDeactivationGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public userHistory = (userId: string) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Employees/GetUserHistory?id=${userId}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new UserHistoryGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public changeRequests = (employeeId: string) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/ChangeRequests/getChangeRequests`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
            ajaxOptions.headers = {
                'EmployeeId': employeeId
            };
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new ChangeRequestGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public emailTemplates = () => AspNetData.createStore({
        key: 'type',
        loadUrl: `/api/Emails/getEmailTemplates`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new EmailTemplateGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public recipientOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Emails/getRecipientOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new RecipientOptionDto();
                data.init(item);
                item = data.value;
            });
            return data;
        }
    });

    public emailLogs = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Emails/getEmailLogs`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new EmailLogGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public assets = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Assets/getAssetList`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new AssetGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public incidentReports = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/IncidentReports/GetIncidentReports`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new IncidentReportGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public checklists = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Checklist/GetChecklists`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new ChecklistGridViewDto();
                data.init(item);
                data.created = createDate(item.created);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public assetOptions = (type: AssetType, showFree: boolean, assetId: string | null = null) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Assets/getAssetOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
            ajaxOptions.headers = {
                'Type': type,
                'ShowFree': showFree ? 'true' : 'false',
                'AssetId': assetId
            };
        }
    });

    public materials = () => AspNetData.createStore({
        key: 'number',
        loadUrl: `/api/Inventory/getMaterials`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new MaterialGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public halbs = () => AspNetData.createStore({
        key: 'number',
        loadUrl: `/api/Inventory/getHalbs`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new HalbGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public bins = () => AspNetData.createStore({
        key: 'number',
        loadUrl: `/api/Inventory/getBins`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new BinGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public storageUnits = () => AspNetData.createStore({
        key: 'number',
        loadUrl: `/api/Inventory/getStorageUnits`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new StorageUnitGridViewDto();
                data.init(item);
                data.sledBbd = createDate(item.sledBbd);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public storageUnitsByBin = (storageTypeCode: string) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Inventory/GetStorageUnitsByBin`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
            ajaxOptions.headers = {
                'StorageTypeCode': storageTypeCode
            };
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new StorageUnitByBinGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public facilityUtilization = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Inventory/getFacilityUtilization`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new FacilityUtilizationGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public countsPerStorageType = (countType: CountType) => AspNetData.createStore({
        key: 'date',
        loadUrl: `/api/Inventory/GetCountsPerStorageType?countType=${countType}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new CountsPerStoreTypeGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public countsPerPalletType = (palletTypeCount: PalletCountType) => AspNetData.createStore({
        key: 'date',
        loadUrl: `/api/Inventory/GetCountsPerPalletType?palletTypeCount=${palletTypeCount}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new CountsPerPalletTypeGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public suppliers = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Suppliers/GetSuppliers`,
        deleteUrl: `/api/Suppliers/DeleteSupplier`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public carriers = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Carriers/GetCarriers`,
        deleteUrl: `/api/Carriers/DeleteCarrier`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public customers = () => new CustomStore({
        key: 'id',
        load: (loadOptions) => {
            let params = this.buildParams(loadOptions);
            return lastValueFrom(this.http.get<any>('/api/Customers/GetCustomers', {params, withCredentials: true}));
        },
        insert: (values) => {
            const command = new CreateCustomerCommand();
            command.init(values);
            return lastValueFrom(this.customersClient.createCustomer(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        update: (key, values) => {
            const command = new UpdateCustomerCommand();
            command.id = key;
            command.init(values);
            return lastValueFrom(this.customersClient.updateCustomer(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        remove: (key) => lastValueFrom(this.customersClient.deleteCustomer(key))
    });

    public supplierOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Suppliers/GetSupplierOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public inboundProductivityEmployeeOptions = () => new DataSource({
        paginate: true,
        pageSize: 50,
        sort: 'name',
        store: AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Appointments/GetInboundProductivityEmployeeOptions`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
            }
        })
    });

    public appointmentComplianceSupplierOptions = () => new DataSource({
        paginate: true,
        pageSize: 50,
        sort: 'name',
        store: AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Appointments/GetAppointmentComplianceSupplierOptions`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
            }
        })
    });

    public carrierOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Carriers/GetCarrierOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public customerOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Customers/GetCustomerOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public agencyOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Agencies/GetAgencyOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public appointments = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Appointments/GetAppointments`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new AppointmentGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public appointmentHistory(number: string | null = null) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Appointments/GetAppointmentHistory`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'Number': number
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new AppointmentHistoryGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public shuntLocationOptions = (type: ShuntLocationType, locationId: string | null = null) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/ShuntLocations/GetShuntLocationOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
            ajaxOptions.headers = {
                'Type': type,
                'LocationId': locationId
            };
        }
    });

    public appointmentCompliances(number: string | null = null) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Appointments/GetAppointmentCompliances`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'Number': number
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new AppointmentComplianceGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public purchaseOrders = () => AspNetData.createStore({
        key: 'purchasingDocument',
        loadUrl: `/api/PurchaseOrders/getPurchaseOrders`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new PurchaseOrderGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public purchaseOrderOptions = (supplierId: string, purchasingDocuments: string[] | null = null) => AspNetData.createStore({
        key: 'purchasingDocument',
        loadUrl: `/api/PurchaseOrders/getPurchaseOrderOptions?arg1=${supplierId}&arg2=${purchasingDocuments}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public shipmentOptions = (selected: string[] = []) => new DataSource({
        paginate: true,
        pageSize: 50,
        store: AspNetData.createStore({
            key: 'logisticalGroup',
            loadUrl: `/api/Orders/getShipmentOptions`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
            },
            // The server returns only the currently eligible C Numbers. Inject any already-selected
            // values that are missing from the result so the tag box can render them as tags (dxTagBox
            // discards selected values that are absent from its data source). Runs on every load, so
            // server-side search keeps working and the selected values are always present.
            onLoaded(data) {
                selected
                    .filter((s) => !data.some((o: any) => o.logisticalGroup === s))
                    .forEach((s) => data.push({logisticalGroup: s}));
                return data;
            }
        })
    });

    public emptyCheckIns = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/EmptyCheckIns/GetEmptyCheckIns`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new EmptyCheckInGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public emptyCheckInHistory(number: string | null = null) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/EmptyCheckIns/GetEmptyCheckInHistory`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'Number': number
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new EmptyCheckInHistoryGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public emptyCheckOuts = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/EmptyCheckOuts/GetEmptyCheckOuts`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new EmptyCheckOutGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public emptyCheckOutHistory(number: string | null = null) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/EmptyCheckOuts/GetEmptyCheckOutHistory`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'Number': number
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new EmptyCheckOutHistoryGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public purchaseOrderItems = (numbers: string[] | null = null) => AspNetData.createStore({
        key: 'material',
        loadUrl: `/api/PurchaseOrders/getPurchaseOrderItems?id=${numbers ? numbers.join(',') : ''}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new PurchaseOrderItemDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public orders = () => AspNetData.createStore({
        key: 'salesDocument',
        loadUrl: `/api/Orders/getOrders`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new OrderGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public orderLines = () => AspNetData.createStore({
        key: ['salesDocument', 'salesDocumentItem', 'productionItem'],
        loadUrl: `/api/Orders/getOrderLines`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new OrderLineDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public loads = () => AspNetData.createStore({
        key: 'logisticalGroup',
        loadUrl: `/api/Orders/getLoads`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new LoadGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public loadLines(number: string | null = null) {
        return AspNetData.createStore({
            key: ['salesDocument', 'salesDocumentItem', 'productionItem'],
            loadUrl: `/api/Orders/GetLoadLines`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'Number': number
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new OrderLineDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public blockedStock() {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Inventory/GetBlockedStock`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new BlockedStockGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public blockedStockScore() {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Inventory/GetBlockedStockScore`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new BlockedStockScoreGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public nextView = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Productivity/GetNextView`,
        deleteUrl: `/api/Productivity/DeleteNextView`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new NextViewGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public yearToDate = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Productivity/GetYearToDate`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new YearToDateGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public performance = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Productivity/GetPerformance`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new PerformanceGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public mb51() {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Inventory/GetMB51`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new MB51GridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public mb51Quantities(reason: MB51Reason) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Inventory/GetMB51Quantities?id=${reason}`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new MB51QuantityGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public productivitySettings = () => new CustomStore({
        key: 'id',
        load: (loadOptions) => {
            let params = this.buildParams(loadOptions);
            return lastValueFrom(this.http.get<any>('/api/Productivity/GetProductivitySettings', {params, withCredentials: true}));
        },
        insert: (values) => {
            const command = new CreateProductivitySettingCommand();
            command.init(values);
            return lastValueFrom(this.productivityClient.createProductivitySetting(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        update: (key, values) => {
            const command = new UpdateProductivitySettingCommand();
            command.id = key;
            command.init(values);
            return lastValueFrom(this.productivityClient.updateProductivitySetting(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        remove: (key) => lastValueFrom(this.productivityClient.deleteProductivitySetting(key))
    });

    public vendors = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetVendors`,
        deleteUrl: `/api/Invoicing/DeleteVendor`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public vendorOptions = (Id: string | null = null) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetVendorOptions${Id ? `?id=${Id}` : ''}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public activeVendorOptions = (serviceTypeId: string | null = null) => new DataSource({
        store: this.vendorOptions(serviceTypeId),
        filter: ['status', '=', VendorStatus.Active],
        paginate: false,
    });

    public glCodes = () => new CustomStore({
        key: 'id',
        load: (loadOptions) => {
            let params = this.buildParams(loadOptions);
            return lastValueFrom(this.http.get<any>('/api/Invoicing/GetGlCodes', {params, withCredentials: true}));
        },
        insert: (values) => {
            const command = new CreateGlCodeCommand();
            command.init(values);
            return lastValueFrom(this.invoicingClient.createGlCode(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        update: (key, values) => {
            const command = new UpdateGlCodeCommand();
            command.id = key;
            command.init(values);
            return lastValueFrom(this.invoicingClient.updateGlCode(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        remove: (key) => lastValueFrom(this.invoicingClient.deleteGlCode(key).pipe(catchError(err => this.handleError(err))))
    });

    public invoicePurchaseOrders = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetInvoicePurchaseOrders`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new InvoicePurchaseOrderGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public invoicePurchaseOrderHistory(number: string | null = null) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Invoicing/GetInvoicePurchaseOrderHistory`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'Number': number
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new InvoicePurchaseOrderHistoryGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public orderHistory(number: string | null = null) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Orders/GetOrderHistory`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'Number': number
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new OrderHistoryGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public serviceTypes = () => new CustomStore({
        key: 'id',
        load: (loadOptions) => {
            let params = this.buildParams(loadOptions);
            return lastValueFrom(this.http.get<any>('/api/Invoicing/GetServiceTypes', {params, withCredentials: true}));
        },
        insert: (values) => {
            const command = new CreateServiceTypeCommand();
            command.init(values);
            return lastValueFrom(this.invoicingClient.createServiceType(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        update: (key, values) => {
            const command = new UpdateServiceTypeCommand();
            command.id = key;
            command.init(values);
            return lastValueFrom(this.invoicingClient.updateServiceType(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        remove: (key) => lastValueFrom(this.invoicingClient.deleteServiceType(key).pipe(catchError(err => this.handleError(err))))
    });

    public serviceTypeOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetServiceTypeOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public availableServiceTypeOptions = () => new DataSource({
        store: this.serviceTypeOptions(),
        filter: ['hasActiveVendors', '=', true],
        paginate: false,
    });

    public glCodeOptions = (module: string | null = null) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetGLCodeOptions${module ? `?id=${module}` : ''}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public vendorEmployeeOptions = (Id: string | null = null) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Employees/GetVendorEmployeeOptions${Id ? `?id=${Id}` : ''}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public glCodeClients = () => new CustomStore({
        key: 'id',
        load: (loadOptions) => {
            let params = this.buildParams(loadOptions);
            return lastValueFrom(this.http.get<any>('/api/Invoicing/GetGlCodeClients', {params, withCredentials: true}));
        },
        insert: (values) => {
            const command = new CreateGlCodeClientCommand();
            command.init(values);
            return lastValueFrom(this.invoicingClient.createGlCodeClient(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        update: (key, values) => {
            const command = new UpdateGlCodeClientCommand();
            command.id = key;
            command.init(values);
            return lastValueFrom(this.invoicingClient.updateGlCodeClient(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        remove: (key) => lastValueFrom(this.invoicingClient.deleteGlCodeClient(key).pipe(catchError(err => this.handleError(err))))
    });

    public glCodeClientOptions = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetGlCodeClientOptions`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public siteOptions = (module: string | null = null) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetSiteOptions${module ? `?id=${module}` : ''}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public billToOptions = (module: string | null = null) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetBillToOptions${module ? `?id=${module}` : ''}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public conversionRates = () => new CustomStore({
        key: 'id',
        load: (loadOptions) => {
            let params = this.buildParams(loadOptions);
            return lastValueFrom(this.http.get<any>('/api/Invoicing/GetConversionRates', {params, withCredentials: true}));
        },
        insert: (values) => {
            const command = new CreateConversionRateCommand();
            command.init(values);
            return lastValueFrom(this.invoicingClient.createConversionRate(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        update: (key, values) => {
            const command = new UpdateConversionRateCommand();
            command.id = key;
            command.init(values);
            return lastValueFrom(this.invoicingClient.updateConversionRate(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        remove: (key) => lastValueFrom(this.invoicingClient.deleteConversionRate(key))
    });

    public mustDepartTimes = () => new CustomStore({
        key: 'id',
        load: (loadOptions) => {
            let params = this.buildParams(loadOptions);
            return lastValueFrom(this.http.get<any>('/api/MustDepartTimes/GetMustDepartTimes', {params, withCredentials: true}));
        },
        insert: (values) => {
            const command = new CreateMustDepartTimeCommand();
            command.init(values);
            return lastValueFrom(this.mustDepartTimesClient.createMustDepartTime(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        update: (key, values) => {
            const command = new UpdateMustDepartTimeCommand();
            command.id = key;
            command.init(values);
            return lastValueFrom(this.mustDepartTimesClient.updateMustDepartTime(command).pipe(
                catchError(err => this.handleError(err))
            ));
        },
        remove: (key) => lastValueFrom(this.mustDepartTimesClient.deleteMustDepartTime(key))
    });

    public invoices = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetInvoices`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new InvoiceGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public invoiceHistory(number: string | null = null) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Invoicing/GetInvoiceHistory`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'Number': number
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new InvoiceHistoryGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public invoicePurchaseOrderOptions = (purchaseOrder: string | null = null) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetInvoicePurchaseOrderOptions${purchaseOrder ? `?id=${purchaseOrder}` : ''}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });

    public creditRequests = () => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetCreditRequests`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        },
        onLoaded(data) {
            data.forEach((item: any) => {
                let data = new CreditRequestGridViewDto();
                data.init(item);
                Object.assign(item, data);
            });
            return data;
        }
    });

    public creditRequestHistory(number: string | null = null) {
        return AspNetData.createStore({
            key: 'id',
            loadUrl: `/api/Invoicing/GetCreditRequestHistory`,
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.xhrFields = {withCredentials: true};
                ajaxOptions.headers = {
                    'Number': number
                };
            },
            onLoaded(data) {
                data.forEach((item: any) => {
                    let data = new CreditRequestHistoryGridViewDto();
                    data.init(item);
                    Object.assign(item, data);
                });
                return data;
            }
        });
    }

    public invoiceOptions = (vendorId: string, invoiceId: string | null = null) => AspNetData.createStore({
        key: 'id',
        loadUrl: `/api/Invoicing/GetInvoiceOptions?arg1=${vendorId}${invoiceId ? `&arg2=${invoiceId}` : ''}`,
        onBeforeSend(method, ajaxOptions) {
            ajaxOptions.xhrFields = {withCredentials: true};
        }
    });
}
